import { supabase } from '../../../lib/supabaseClient';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { InterviewListItem, Interview, CreateInterviewPayload, UpdateInterviewPayload, CreateInterviewBulkPayload } from '../types';
import { ApiResponse } from '../../../types/api';
import { handleEdgeFunctionError } from '../../../utils/edgeFunctionUtils';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Load interview schedule for a job using Edge Function
 * This calls the serverless function which does all the multi-table queries
 */
export async function listInterviewsForJobWithEdgeFunction(jobId: string): Promise<ApiResponse<InterviewListItem[]>> {
  try {
    const { data, error } = await supabase.functions.invoke('get-interviews-for-job', {
      body: { jobId },
      // Note: Authorization header is automatically added by supabase client
    });

    if (error) {
      console.error('[Edge Function Error]', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: [], error: extractedError };
    }

    return { data: data?.data || [], error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception]', err);
    return { data: [], error: err };
  }
}

export async function updateInterviewWithEdgeFunction(payload: UpdateInterviewPayload): Promise<ApiResponse<Interview>> {
  try {
    const { data, error } = await supabase.functions.invoke('update-interview', {
      body: payload,
    });

    if (error) {
      console.error('[Edge Function Error] updateInterview:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: data?.data || null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] updateInterview:', err);
    return { data: null, error: err };
  }
}

export async function createInterviewWithEdgeFunction(payload: CreateInterviewPayload): Promise<ApiResponse<Interview>> {
  try {
    const { data, error } = await supabase.functions.invoke('create-interview', {
      body: payload,
    });

    if (error) {
      console.error('[Edge Function Error] createInterview:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: null, error: extractedError };
    }

    return { data: data?.data || null, error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] createInterview:', err);
    return { data: null, error: err };
  }
}

export async function createInterviewBulkWithEdgeFunction(payload: CreateInterviewBulkPayload): Promise<ApiResponse<Interview[]>> {
  try {
    const { data, error } = await supabase.functions.invoke('create-interview-bulk', {
      body: payload,
    });

    if (error) {
      console.error('[Edge Function Error] createInterviewBulk:', error);
      const extractedError = await handleEdgeFunctionError(error);
      return { data: [], error: extractedError };
    }

    return { data: data?.data || [], error: data?.error || null };
  } catch (err) {
    console.error('[Edge Function Exception] createInterviewBulk:', err);
    return { data: [], error: err };
  }
}
